import createSelector from 'selectorator'
import { identity, uniqBy } from 'ramda'
import { RootState } from 'src/state/types'

import { NAMESPACE } from '../constants'
import { TApiError, TApiErrors, TApiEvent, TApiStateState } from '../types'
import { requestKey } from '.'

const EMPTY_ERRORS: TApiError[] = []

export const apiState = createSelector<RootState, TApiStateState>([NAMESPACE], identity)

/**
 * Get a slice of the API state.
 *
 * NB no `api.` prefix — unlike the other API modules this reducer is mounted at
 * the root of the store by `state/root.ts`, not by `modules/api/reducer.ts`.
 */
const getSlice = <K extends keyof TApiStateState>(key: K) =>
  createSelector<RootState, TApiStateState[K]>([`${NAMESPACE}.${key}`], identity)

const loading = getSlice('loading')

const errors = getSlice('errors')

export const isLoading = createSelector<RootState, (events: TApiEvent[]) => boolean>(
  [loading],
  (loading: TApiStateState['loading']) => events => events.some(event => !!loading[requestKey(event.type)]),
)

const errorsForEvent = (errors: TApiStateState['errors'], event: TApiEvent): TApiErrors | undefined => {
  const eventErrors = errors[requestKey(event.type)]
  if (!event.payload) return eventErrors
  // A `reduceByPayload: false` key holds a bare list, which has no payload buckets to look in.
  return Array.isArray(eventErrors) ? undefined : eventErrors?.[JSON.stringify(event.payload)]
}

/**
 * Collapse an entry into a single list of errors. Payload-bucketed entries take
 * the first bucket, which is only meaningful for a form: a form only ever has
 * one submission in flight.
 */
const toErrorList = (entry: TApiErrors | undefined): TApiError[] => {
  if (entry == null) return EMPTY_ERRORS
  if (Array.isArray(entry)) return entry
  return Object.values(entry)[0] ?? EMPTY_ERRORS
}

const formatApiErrorResponse = (errors: TApiError[]): Record<string, string> => {
  const getFieldKey = (key: string): string => key.split('.')[0]
  const formattedErrors: Record<string, string> = {}
  // `uniqBy` keeps the first error per field, so `address.line1` wins over `address.city`.
  uniqBy(error => getFieldKey(error.key), errors).forEach(error => {
    formattedErrors[getFieldKey(error.key)] = error.message
  })
  return formattedErrors
}

/**
 * Get errors for a specifc Redux action (event).
 * EG: getErrorsByEvent(state)(userApiEvents.login_request)
 *
 * Get errors for a specific Redux action (event) with a payload
 * EG: getErrorsByEvent(state)(userApiEvents.login_request({ email: 'test@example.com', password: '123123' }))
 */
export const getErrorsByEvent = createSelector<RootState, (event: TApiEvent) => TApiErrors | undefined>(
  [errors],
  (errors: TApiStateState['errors']) => event => errorsForEvent(errors, event),
)

/** The `general` messages for an event, for display above a form. */
export const getGeneralFormErrorsByEvent = createSelector<RootState, (event: TApiEvent) => string[]>(
  [errors],
  (errors: TApiStateState['errors']) => event =>
    toErrorList(errorsForEvent(errors, event))
      .filter(error => error.key === 'general')
      .map(error => error.message),
)

/** The field-level messages for an event, keyed by field name for Formik's `setErrors`. */
export const getFormFieldErrorsByEvent = createSelector<RootState, (event: TApiEvent) => Record<string, string>>(
  [errors],
  (errors: TApiStateState['errors']) => event =>
    formatApiErrorResponse(toErrorList(errorsForEvent(errors, event)).filter(error => error.key !== 'general')),
)
